/**
 * React component representing the image section for "Hey Matic."
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - Data for the component, including information for the "Hey Matic" image section.
 * @returns {JSX.Element} React component.
 */

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import LazyVideoNew from "../LazyVideo/LazyVideoNew";
import { motion, useInView, useAnimation } from "framer-motion";
import maticMoving from "@assets/images/homepage/matic_moving.webp";
import maticSchedule from "@assets/images/homepage/matic_schedule.webp";
import maticMovingTop from "@assets/images/homepage/matic_moving_top.webp";

const transitionLine = { duration: 10, ease: "easeInOut" };

export default function HeyMaticImageSection({ data }) {
  const ref1 = useRef();
  const robotTrackInView = useInView(ref1, { once: true, amount: 0.3 });

  const controls = useAnimation();
  const pathControls = useAnimation();

  useEffect(() => {
    if (robotTrackInView) {
      controls.start({ offsetDistance: "100%" });
      pathControls.start({ pathLength: 1 });
    }
    return () => {
      controls.stop();
      pathControls.stop();
    };
  }, [controls, robotTrackInView]);
  return (
    <>
      <div className="lg:grid hidden">
        <div className="mr-[80px] ml-[40px]">
          <div className="w-[100%] relative">
            <div className="aspect-w-33 aspect-h-23">
              <div className="h-full w-full">
                <div className="h-full relative">
                  <div
                    className={`w-[48.48%] absolute right-0 top-[4.4%] bg-white`}
                  >
                      <div className="aspect-w-640 aspect-h-640 relative">
                        <Image
                          src={maticMoving}
                          alt="Matic Image"
                          className="h-full w-full object-cover object-center rounded-[16px]"
                        />
                      </div>
                  </div>
                  <div className={`absolute left-[48%] top-0`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-butter rounded-[16px]">
                        <h6
                          className={`md:flex hidden text-center py-[12px] text-primary px-[11px]`}
                        >
                          Spot clean spills from your phone
                        </h6>
                      </div>
                  </div>
                  <div
                    className={`w-[38.18%] absolute left-[18.5%] top-[11.5%] -z-[1]`}
                    ref={ref1}
                  >
                    <div className="aspect-w-280 aspect-h-259 relative">
                      <svg
                        width="522"
                        height="564"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <motion.path
                          d="M621 3H131C60.3076 3 3 60.3076 3 131V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                        />
                        <motion.path
                          d="M621 40H136C82.9807 40 40 82.9807 40 136V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                        />
                        <motion.path
                          d="M621 76H140C105.206 76 77 104.206 77 139V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                        />
                      </svg>
                      <motion.div
                        className={`maticRobotContainer w-[182px] h-[136px]`}
                        initial={{ offsetDistance: "0%" }}
                        animate={controls}
                        transition={transitionLine}
                      >
                        <Image
                          src={maticMovingTop}
                          alt="Moving Robot"
                          width={182}
                          height={136}
                          sizes="182px"
                        />
                      </motion.div>
                    </div>
                  </div>
                  <div
                    className={`w-[37.47%] absolute left-[3.2%] bottom-[8.7%] bg-white`}
                  >
                      <div className="aspect-w-480 aspect-h-480 relative">
                        <LazyVideoNew
                          videoImagePath={data.heyMaticVideoComponent2}
                          isMobile={false}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px]`}
                        />
                      </div>
                  </div>
                  <div className={`absolute left-0 top-[33%]`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-butter rounded-[16px]">
                        <h6
                          className={`md:flex hidden text-center py-[12px] text-primary px-[32px]`}
                        >
                          {data?.easyInAppSchedulingText}
                        </h6>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* flex for mobile*/}
      <div
        className={`flex flex-col justify-center items-center lg:hidden pt-[40px] h-full px-[24px] md:px-0`}
      >
        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideoNew
                videoImagePath={data.heyMaticVideoComponent1}
                isMobile={true}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-butter rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[12%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center text-primary px-[21px] py-[12px]`}
              >
                Spot clean spills from your phone
              </h6>
            </div>
        </div>
        <div className="lg:hidden block mb-[40px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <Image
                src={maticSchedule}
                alt="Moving Robot"
                width={327}
                height={327}
                sizes="(max-width: 560px) 100vw, 560px"
                className="rounded-[16px]"
              />
            </div>
            <div
              className={`bg-butter rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[7%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center text-primary px-[21px] py-[12px]`}
              >
                {data?.easyInAppSchedulingText}
              </h6>
            </div>
        </div>
      </div>
    </>
  );
}
